import React from 'react';

/**
 * EscalaRating — Componente de valoración 1 a 10 con fill rating.
 *
 * Usado en preguntas de tipo `escala_1_10` dentro de Pruebas y Exámenes.
 * NO calificable: solo recoge feedback del estudiante (estilo NPS).
 *
 * Comportamiento del fill rating: al tocar un número N, se "rellenan" todos
 * los cuadrados del 1 al N con un gradiente rojo→naranja→verde. El cuadrado
 * exactamente seleccionado se destaca con borde más grueso. Inspirado en
 * la UX de Uber/Airbnb pero con escala 1-10 (estándar uruguayo).
 *
 * Props:
 *   value (number|null)      — valor actualmente seleccionado (1-10) o null
 *   onChange (fn(n)=>void)   — callback al cambiar selección. Si no se pasa
 *                              o readOnly=true, el componente no es interactivo
 *   etiquetaIzq (string?)    — texto debajo/al lado del 1 (ej. "No me enganchó")
 *   etiquetaDer (string?)    — texto debajo/al lado del 10 (ej. "Excelente")
 *   readOnly (boolean)       — modo solo-lectura (profesor viendo respuesta)
 */
const EscalaRating = ({ value, onChange, etiquetaIzq, etiquetaDer, readOnly = false }) => {
  const selected = typeof value === 'string' ? parseInt(value, 10) : value;
  const numericValue = Number.isInteger(selected) && selected >= 1 && selected <= 10 ? selected : null;

  // Gradiente: 1-3 rojo, 4-6 naranja, 7-10 verde (intensidad creciente)
  const getCellColor = (n) => {
    if (n <= 3) return 'bg-red-400 border-red-500 text-white';
    if (n <= 6) return 'bg-orange-400 border-orange-500 text-white';
    return 'bg-emerald-500 border-emerald-600 text-white';
  };

  const cells = Array.from({ length: 10 }, (_, i) => i + 1);

  return (
    <div className="w-full" data-testid="escala-rating">
      {(etiquetaIzq || etiquetaDer) && (
        <div className="flex justify-between text-xs text-gray-600 mb-2 px-1">
          <span data-testid="escala-rating-etiqueta-izq">{etiquetaIzq || ''}</span>
          <span data-testid="escala-rating-etiqueta-der">{etiquetaDer || ''}</span>
        </div>
      )}
      <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
        {cells.map((n) => {
          const isFilled = numericValue !== null && n <= numericValue;
          const isExact = numericValue === n;
          const fillClasses = isFilled
            ? getCellColor(n)
            : 'bg-gray-100 border-gray-300 text-gray-500';
          const exactClasses = isExact
            ? 'ring-2 ring-offset-2 ring-indigo-500 scale-105'
            : '';
          const interactiveClasses = readOnly || !onChange
            ? 'cursor-default'
            : 'cursor-pointer hover:scale-105 hover:shadow-md active:scale-95';

          return (
            <button
              key={n}
              type="button"
              onClick={() => !readOnly && onChange && onChange(n)}
              disabled={readOnly || !onChange}
              className={`
                aspect-square rounded-xl border-2 font-bold text-xl
                transition-all duration-150
                ${fillClasses} ${exactClasses} ${interactiveClasses}
              `}
              data-testid={`escala-rating-cell-${n}`}
              aria-label={`${n} de 10`}
              aria-pressed={isExact}
            >
              {n}
            </button>
          );
        })}
      </div>
      {numericValue !== null && (
        <div className="mt-3 text-center text-sm text-gray-700">
          Tu valoración: <span className="font-bold text-lg text-indigo-700" data-testid="escala-rating-valor-actual">{numericValue}/10</span>
        </div>
      )}
    </div>
  );
};

export default EscalaRating;
